import React, { useState, useEffect } from 'react';
import { useGenerateSecret } from '@src/hooks/useGenerateSecret';
import PropTypes from 'prop-types';
import { Button } from '@uhg-abyss/web/ui/Button';
import { styled } from '@uhg-abyss/web/tools/styled';
import { event } from '@uhg-abyss/web/tools/event';
import { DisplayField } from './DisplayField';
import { ConfirmationModal } from '../ConfirmationModal';
import { useGetCredentials } from '@src/hooks/useGetCredentials';

import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';

const Wrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '18px',
  width: '100%',
  background: '#ffffff',
  border: '1px solid #000000',
  boxSizing: 'border-box',
  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
});

const SecretWrapper = styled('div', {
  marginTop: '20px',
});

const StyledBoxHeading = styled('p', {
  fontStyle: 'normal',
  fontWeight: 'bold',
  fontSize: '25.63px',
  lineHeight: '32px',
  color: '#002677',
  margin: '0',
});

const HeaderContainer = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  marginBottom: '10px',
});

const InpContainer = styled('div', {
  padding: '10px',
  width: '100%',
  '.label': {
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: '15px',
    lineHeight: '18px',
    color: '#333333',
    marginBottom: '0',
  },
});

const DetailContainer = styled('div', {
  paddingTop: '10px',
  '.line1': {
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: '15px',
    lineHeight: '20px',
    color: '#000000',
  },
  '.secretg': {
    fontWeight: 'normal',
    fontSize: '14px',
  },
});

const NonData = styled('div', {
  padding: '20px 10px 10px',
  p: {
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '15px',
    lineHeight: '20px',
    color: '#000000',
  },
});

const GenerateRequiredError = styled('p', {
  color: 'red',
  fontSize: '15px',
});

// --- Refactored: SecretListBlock ---
const SecretListBlock = ({
  secrets,
  isSecretKeeper,
  heading,
  isDeleteTriggered,
}) => {
  if (!Array.isArray(secrets) || secrets.length === 0) return null;

  return secrets.map((secretObj, idx) => {
    const { hint, endDateTime, keyId, isNewlyCreated = false } = secretObj;
    return (
      <SecretWrapper key={idx}>
        {hint && (
          <React.Fragment>
            <DisplayField
              id={`${heading}-secret-${idx}`}
              label="Secret"
              value={isNewlyCreated ? hint : `${hint}****************`}
              keyId={keyId}
              environment={heading}
              setShowDeleteModal={isDeleteTriggered}
              isNewlyCreated={isNewlyCreated}
              isSecretKeeper={isSecretKeeper}
            />
          </React.Fragment>
        )}
        <DetailContainer>
          <p id={`secret-expires-${idx}`} className="line1">
            Expires on:{' '}
            <span className="secretg">
              {endDateTime ? `${endDateTime} EST` : ''}
            </span>
          </p>
        </DetailContainer>
      </SecretWrapper>
    );
  });
};

export const Box = ({
  clientId,
  secrets,
  heading,
  showData,
  isSecretKeeper,
  description,
  regenSecret,
  setRegenSecretEnv,
  isSecretGenerated,
  orgId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [displayGenerateError, setDisplayGenerateError] = useState(false);
  const [errorEnv, setErrorEnv] = useState('');
  const [generatedSecret] = useGenerateSecret();
  const [confirmationTitle, setConfirmationTitle] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [deletePayloadObj, setDeletePayloadObj] = useState(null);
  const [credentialsInfo] = useGetCredentials();

  const isDeleteTriggered = (keyId, environment) => {
    const isProd = environment === 'Production';
    setConfirmationTitle(`Are you sure?`);
    setDeletePayloadObj({ keyId, isProd, orgId, clientId });
    setShowConfirmationModal(true);
  };

  useEffect(() => {
    setIsLoading(generatedSecret.loading);
    if (!generatedSecret.loading && generatedSecret.error) {
      setDisplayGenerateError(true);
    }
  }, [generatedSecret]);

  useEffect(() => {
    if (!credentialsInfo.loading) {
      closeConfirmation();
    }
  }, [credentialsInfo]);

  const regenerateSecret = () => {
    setRegenSecretEnv(heading === 'Production' ? 'prod' : 'stage');
    setDisplayGenerateError(false);
    regenSecret(true);
    setErrorEnv(heading);
    event('JOB_START', { event, jobName: 'regenerate secret key' });
    event('MODAL_VIEW', {
      event,
      modalName: 'change API secret key modal pop up',
    });
  };

  const closeConfirmation = () => {
    setShowConfirmationModal(false);
  };

  const generateAdditionalSecretButton = () => {
    return secrets && secrets.length >= 2 ? (
      <Tooltip content="you can generate up to 2 secrets. Remove one before adding a new one.">
        <Button
          size="$md"
          variant="outline"
          onClick={regenerateSecret}
          ariaLoadingLabel="Generating Secret"
          id="regenerate-secret-button"
          disabled={true}
        >
          Generate additional secret
        </Button>
      </Tooltip>
    ) : (
      <Button
        size="$md"
        variant="outline"
        onClick={regenerateSecret}
        ariaLoadingLabel="Generating Secret"
        id="regenerate-secret-button"
        disabled={false}
      >
        Generate additional secret
      </Button>
    );
  };

  return (
    <Wrapper>
      {showConfirmationModal && (
        <ConfirmationModal
          title={confirmationTitle}
          isOpen={showConfirmationModal}
          isClosed={setShowConfirmationModal}
          handleConfirmModal={closeConfirmation}
          deletePayloadObj={deletePayloadObj}
        />
      )}

      <HeaderContainer>
        <StyledBoxHeading id={`${heading}`}>{heading}</StyledBoxHeading>
        {showData &&
          isSecretKeeper &&
          (isSecretGenerated ? (
            generateAdditionalSecretButton()
          ) : (
            <Button
              size="$md"
              variant="outline"
              onClick={regenerateSecret}
              isLoading={isLoading}
              id="generate-secret-button"
            >
              Generate Secret
            </Button>
          ))}
      </HeaderContainer>
      {showData ? (
        <InpContainer>
          <DisplayField
            label="Client Id"
            value={clientId}
            environment={heading}
          />
          <SecretListBlock
            secrets={secrets}
            isSecretKeeper={isSecretKeeper}
            heading={heading}
            isDeleteTriggered={isDeleteTriggered}
          />
          {displayGenerateError && errorEnv === heading && (
            <GenerateRequiredError id="regenerate-secret-error">
              The secret generation was unsuccessful, please try again.
            </GenerateRequiredError>
          )}
        </InpContainer>
      ) : (
        <NonData>
          <p id="description">{description}</p>
        </NonData>
      )}
    </Wrapper>
  );
};

Box.propTypes = {
  clientId: PropTypes.string.isRequired,
  secrets: PropTypes.array.isRequired,
  heading: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  showData: PropTypes.bool.isRequired,
  isSecretKeeper: PropTypes.bool.isRequired,
  regenSecret: PropTypes.func.isRequired,
  setRegenSecretEnv: PropTypes.func.isRequired,
  isSecretGenerated: PropTypes.bool.isRequired,
  orgId: PropTypes.string.isRequired,
};
